/**
 * Airtable Field Mapper
 * Transforms Airtable field names to TypeScript interface field names
 *
 * This provides a clean mapping layer between Airtable's space-separated
 * field names and our camelCase TypeScript interfaces.
 */

import type { CallData } from "@/types";

/**
 * Call Data table field mappings
 * Maps Airtable field names -> TypeScript field names
 *
 * Based on actual Airtable fields from "Call Data" table (21 fields):
 * @see scripts/lookup-record.ts for field documentation
 */
export const CALL_DATA_FIELD_MAP: Record<string, string> = {
  // Identity fields
  "Call ID": "callId",
  "Call Data Record": "callDataRecord",
  "Customer Name": "customerName",
  "Email": "clientEmail",
  "Phone Number": "phoneNumber",

  // Call metadata
  "Type": "callType",
  "Ended Reason": "endedReason",
  "Duration Seconds": "durationSeconds",
  "Journeyed": "journeyed",
  "Created": "createdTime",

  // Recording & Transcript
  "Call Recording": "recordingUrl",
  "Transcript": "transcript",

  // AI Analysis
  "Summary": "summary",
  "Insights and Reports": "insightsAndReports",
  "High Level Consult": "highLevelConsult",

  // Attachments
  "Call Image": "callImage",

  // Linked records
  "Client Database": "clientDatabase",
  "Customer Record (from Client DB)": "customerRecord",
  "Submission ID": "submissionId",

  // VAPI integration
  "Vapi Response - Status Code": "vapiStatusCode",
  "Vapi Response - Data": "vapiResponseData",
};

/**
 * Reverse mapping for sending data back to Airtable
 */
export const CALL_DATA_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(CALL_DATA_FIELD_MAP).map(([k, v]) => [v, k])
);

/**
 * Normalize a single Airtable record's fields to camelCase
 * Preserves both the original and normalized field names for flexibility
 */
export function normalizeCallDataFields(
  fields: Record<string, unknown>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};

  for (const [airtableKey, value] of Object.entries(fields)) {
    // Add the normalized key
    const normalizedKey = CALL_DATA_FIELD_MAP[airtableKey];
    if (normalizedKey) {
      normalized[normalizedKey] = value;
    }
    // Also preserve the original key for backward compatibility
    normalized[airtableKey] = value;
  }

  return normalized;
}

/**
 * Transform a full Airtable record (with id, fields, createdTime)
 * into a normalized CallData object
 */
export function transformToCallData(record: {
  id: string;
  fields: Record<string, unknown>;
  createdTime?: string;
}): CallData {
  const normalized = normalizeCallDataFields(record.fields);

  return {
    id: record.id,
    createdTime: record.createdTime,
    ...normalized,
  } as CallData;
}

/**
 * Check if a resource is the Call Data table (by name or ID)
 */
export function isCallDataTable(resource: string): boolean {
  return resource === "Call Data" || resource === "tblMLpCB2q4EpVEdJ";
}
